import React, { useState, useEffect, useContext } from "react";
import { Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function Resources() {
  const [resourcesData, setResourcesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "Resources", path: "/superadmin/resources", isLast: true },
  ];

  const createButtons = [
    {
      text: "Add Resource",
      onClick: () => setIsCreateModalOpen(true),
    },
  ];

  const [formData, setFormData] = useState({
    resourceTitle: "",
    resourceDescription: "",
    resourceLink: "",
    forRole: [],
    isPublic: true,
    thumbnail: null,
    displayOrder: "",
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    resourceTitle: "",
    resourceDescription: "",
    resourceLink: "",
    forRole: [],
    isPublic: true,
    thumbnail: null,
    thumbnailChanged: false,
    thumbnailURL: "",
    displayOrder: "",
  });

  const [formError, setFormError] = useState("");

  // Fetch all resources
  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/resources/get-all`,
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched resources:", data);
        setResourcesData(data.data || data);
      } else {
        throw new Error("Failed to fetch resources");
      }
    } catch (err) {
      setError(err.message);
      swalError("Error", "Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles (you can customize this based on your available roles)
  const fetchRoles = () => {
    const roles = [
      { label: "Admin", value: "admin" },
      { label: "Instructor", value: "instructor" },
      { label: "Student", value: "student" },
    ];
    setRoleOptions(roles);
  };

  useEffect(() => {
    fetchRoles();
    fetchResources();
  }, []);

  // Delete resource
  const handleDelete = async (id, title) => {
    const result = await swalConfirm(
      "Delete Resource",
      `Are you sure you want to delete "${title}"?`,
      "Yes, delete it!",
      "No, Cancel"
    );
    if (result.isConfirmed) {
      try {
        setIsDeleting(true);
        setDeletingId(id);
        const response = await fetchWithAuth(
          `${serverUrl}/api/resources/delete/${id}`,
          {
            method  : "DELETE",
            credential : "include",
          }
        );
        if (response.ok) {
          swalSuccess("Deleted", "Resource deleted successfully");
          fetchResources();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete resource");
        }
      } catch (err) {
        swalError("Error", err.message);
      } finally {
        setIsDeleting(false);
        setDeletingId(null);
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file && file.size > 2 * 1024 * 1024) {
        swalError("Error", "File size must be less than 2MB");
        e.target.value = null;
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: file || null }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleRemoveThumbnail = () => {
    setEditFormData((prev) => ({
      ...prev,
      thumbnail: null,
      thumbnailURL: "",
      thumbnailChanged: true,
    }));
    setIsEditChanged(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file && file.size > 2 * 1024 * 1024) {
        swalError("Error", "File size must be less than 2MB");
        e.target.value = null;
        return;
      }
      setEditFormData((prev) => ({
        ...prev,
        [name]: file || null,
        thumbnailChanged: true,
      }));
      setIsEditChanged(true);
    } else {
      setEditFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
      setIsEditChanged(true);
    }
  };

  // Handle select changes
  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
  };

  const handleEditSelectChange = (selectedOption, { name }) => {
    setEditFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    setIsEditChanged(true);
  };

  // Create resource
  const handleCreateResource = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.resourceTitle.trim()) {
      setFormError("Resource title is required");
      return;
    }
    if (!formData.resourceLink.trim()) {
      setFormError("Resource link is required");
      return;
    }
    if (!formData.forRole || formData.forRole.length === 0) {
      setFormError("Role is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const form = new FormData();
      form.append("resourceTitle", formData.resourceTitle);
      form.append("resourceDescription", formData.resourceDescription);
      form.append("resourceLink", formData.resourceLink);
      form.append("forRole", JSON.stringify(formData.forRole.map((role) => role.value)));
      form.append("isPublic", formData.isPublic);
      form.append("displayOrder", formData.displayOrder);
      if (formData.thumbnail) {
        form.append("thumbnail", formData.thumbnail);
      }

      const response = await fetchWithAuth(`${serverUrl}/api/resources/create`, {
        method: "POST",
        body: form,
      });

      if (response.ok) {
        swalSuccess("Created", "Resource created successfully");
        setFormData({
          resourceTitle: "",
          resourceDescription: "",
          resourceLink: "",
          forRole: [],
          isPublic: true,
          thumbnail: null,
          displayOrder: "",
        });
        setIsCreateModalOpen(false);
        fetchResources();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create resource");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit resource
  const handleEditResource = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!editFormData.resourceTitle.trim()) {
      setFormError("Resource title is required");
      return;
    }
    if (!editFormData.resourceLink.trim()) {
      setFormError("Resource link is required");
      return;
    }
    if (!editFormData.forRole || editFormData.forRole.length === 0) {
      setFormError("Role is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const form = new FormData();
      form.append("resourceTitle", editFormData.resourceTitle);
      form.append("resourceDescription", editFormData.resourceDescription);
      form.append("resourceLink", editFormData.resourceLink);
      form.append("forRole", JSON.stringify(editFormData.forRole.map((role) => role.value)));
      form.append("isPublic", editFormData.isPublic);
      form.append("displayOrder", editFormData.displayOrder);
      if (editFormData.thumbnailChanged) {
        if (editFormData.thumbnail) {
          form.append("thumbnail", editFormData.thumbnail);
        } else {
          form.append("thumbnail", "null");
        }
      }

      const response = await fetchWithAuth(`${serverUrl}/api/resources/update/${editFormData.id}`, {
        method: "PUT",
        body: form,
      });

      if (response.ok) {
        swalSuccess("Updated", "Resource updated successfully");
        setIsEditModalOpen(false);
        fetchResources();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update resource");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
      setIsEditChanged(false);
    }
  };

  // Open edit modal
  const openEditModal = (resource) => {
    setEditFormData({
      id: resource._id,
      resourceTitle: resource.resourceTitle,
      resourceDescription: resource.resourceDescription || "",
      resourceLink: resource.resourceLink,
      forRole: resource.forRole
        ? (Array.isArray(resource.forRole) 
            ? resource.forRole.map((val) => roleOptions.find((r) => r.value === val)).filter(Boolean)
            : [roleOptions.find((r) => r.value === resource.forRole)].filter(Boolean))
        : [],
      isPublic: resource.isPublic,
      displayOrder: resource.displayOrder ?? "",
      thumbnail: null,
      thumbnailChanged: false,
      thumbnailURL: resource.thumbnailURL || resource.thumbnail || resource.thumbnailImage || "",
    });
    setIsEditChanged(false);
    setFormError("");
    setIsEditModalOpen(true);
  };

  // Columns for table
  const columns = [
    {
      key: "displayOrder",
      label: "Order",
      sortable: true,
      render: (row) => <span>{row.displayOrder ?? "-"}</span>,
    },
    { key: "resourceTitle", label: "Title", sortable: true },
    { 
      key: "forRole", 
      label: "Role", 
      sortable: true,
      render: (row) => Array.isArray(row.forRole) ? row.forRole.join(", ") : row.forRole,
    },
    {
      key: "isPublic",
      label: "Public",
      sortable: true,
      render: (row) => (row.isPublic ? "Yes" : "No"),
    },
    {
      key: "resourceLink",
      label: "Link",
      sortable: false,
      render: (row) => (
        <a
          href={row.resourceLink}
          target="_blank"
          rel="noopener noreferrer"
          className="view-btn"
        >
          <i className="fa-solid fa-link"></i>
        </a>
      ),
    },
    {
      key: "edit",
      label: "Edit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          onClick={() => openEditModal(row)}
          title="Edit Resource"
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    {
      key: "delete",
      label: "Delete",
      sortable: false,
      render: (row) => (
        <button
          className="delete-btn"
          onClick={() => handleDelete(row._id, row.resourceTitle)}
          disabled={isDeleting && deletingId === row._id}
          title="Delete Resource"
        >
          {isDeleting && deletingId === row._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash"></i>
          )}
        </button>
      ),
    },
  ];

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/" />;
  }

  if (error && resourcesData.length === 0) {
    return (
      <div>
        <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
        <div className="container" style={{ marginTop: "20px" }}>
          <h2 style={{ color: "red" }}>Error: {error}</h2>
          <button onClick={() => fetchResources()} className="btn-submit">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
      <div className="container" style={{ marginTop: "20px" }}>
        {loading ? (
          <Loader />
        ) : resourcesData.length > 0 ? (
          <Table 
            columns={columns} 
            data={[...resourcesData].sort((a, b) => {
              const aOrder = a.displayOrder ?? -1;
              const bOrder = b.displayOrder ?? -1;
              return aOrder - bOrder;
            })} 
            caption="Resources" 
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            <p>No resources found. Create one to get started!</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add New Resource"
      >
        <form onSubmit={handleCreateResource}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label>Resource Title *</label>
            <input
              type="text"
              name="resourceTitle"
              value={formData.resourceTitle}
              onChange={handleInputChange}
              placeholder="Enter resource title"
              required
            />
          </div>

          <div className="form-group">
            <label>Resource Description</label>
            <textarea
              name="resourceDescription"
              value={formData.resourceDescription}
              onChange={handleInputChange}
              placeholder="Enter resource description"
              rows="2"
              style={{height: "5rem"}}
            ></textarea>
          </div>

          <div className="form-group">
            <label>Thumbnail Image (up to 2MB)</label>
            <input
              type="file"
              name="thumbnail"
              accept="image/*"
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Resource Link *</label>
            <input
              type="url"
              name="resourceLink"
              value={formData.resourceLink}
              onChange={handleInputChange}
              placeholder="https://example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>For Role *</label>
            <Select
              isMulti
              options={roleOptions}
              value={formData.forRole}
              onChange={handleSelectChange}
              name="forRole"
              placeholder="Select roles"
            />
          </div>

          <div className="form-group">
            <label>
              Make Public
            </label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label">{formData.isPublic ? 'Yes' : 'No'}</span>
            </label>
          </div>

          <div className="form-group">
            <label>
              Display Order
            </label>
            <input
              type="number"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              placeholder="Enter display order (e.g., 1)"
            />
          </div>

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Resource"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Resource"
      >
        <form onSubmit={handleEditResource}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label>Resource Title *</label>
            <input
              type="text"
              name="resourceTitle"
              value={editFormData.resourceTitle}
              onChange={handleEditInputChange}
              placeholder="Enter resource title"
              required
            />
          </div>

          <div className="form-group">
            <label>Resource Description</label>
            <textarea
              name="resourceDescription"
              value={editFormData.resourceDescription}
              onChange={handleEditInputChange}
              placeholder="Enter resource description"
              rows="2"
              style={{height: "5rem"}}
            ></textarea>
          </div>

          <div className="form-group">
            <label>Thumbnail Image (up to 2MB)</label>
            <input
              type="file"
              name="thumbnail"
              accept="image/*"
              onChange={handleEditInputChange}
            />
            {editFormData.thumbnailURL && (
              <div style={{ marginTop: "12px" }}>
                <p style={{ margin: "0 0 6px", fontSize: "0.85rem", color: "#555" }}>
                  Current Thumbnail: <b>{editFormData.thumbnailURL.split("/").pop()}</b>
                </p>
                <img
                  src={editFormData.thumbnailURL}
                  alt="Current Thumbnail"
                  style={{ maxWidth: "200px", borderRadius: "6px", display: "block" }}
                />
                <button
                  type="button"
                  className="remove-logo-btn"
                  style={{
                    marginTop: "8px",
                    background: "#e74c3c",
                    color: "#fff",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                  onClick={handleRemoveThumbnail}
                  disabled={isSubmitting}
                >
                  <i className="fa-solid fa-xmark"></i> Remove Thumbnail
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Resource Link *</label>
            <input
              type="url"
              name="resourceLink"
              value={editFormData.resourceLink}
              onChange={handleEditInputChange}
              placeholder="https://example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>For Role *</label>
            <Select
              isMulti
              options={roleOptions}
              value={editFormData.forRole}
              onChange={handleEditSelectChange}
              name="forRole"
              placeholder="Select roles"
            />
          </div>

          <div className="form-group">
            <label>
              Make Public
            </label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="isPublic"
                checked={editFormData.isPublic}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label">{editFormData.isPublic ? 'Yes' : 'No'}</span>
            </label>
          </div>

          <div className="form-group">
            <label>
              Display Order
            </label>
            <input
              type="number"
              name="displayOrder"
              value={editFormData.displayOrder}
              onChange={handleEditInputChange}
              placeholder="Enter display order (e.g., 1)"
            />
          </div>

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || !isEditChanged}
            >
              {isSubmitting ? "Updating..." : "Update Resource"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
